/* eslint-disable camelcase */

import ReduxService from 'src/redux/redux.service';
import { TrendsData, TrendEvent } from './results.type';
import { RootState } from 'src/redux/redux-store';
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { createShare } from 'src/services/surveys/surveys.helpers';
import { APITagType } from 'src/redux/api/invisibly-api.types';

const getUrl = (url: string) => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? url.replace('b2b', 'client') : url;
};

const getClientId = () => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? state.userState.activeClient?.ext_client_id ?? null : null;
};


interface GetTrendsDataParams {
  ext_survey_id: string;
  share?: boolean;
  interval?: number;
  start_date?: string;
  end_date?: string;
  selected_survey_answers?: string[];
}

interface SerializeQueryArgsParams {
  queryArgs: GetTrendsDataParams;
}

/** Payload for creating a trend event (no id or created_at). */
export interface CreateTrendEventBody {
  short_title: string;
  long_title: string;
  // The backend model uses a ForeignKey `survey`, and the API expects both
  // the FK (`survey`) and the survey identifier (`survey_id`) in the payload.
  survey?: string;
  survey_id?: string;
  description?: string | null;
  image_url?: string | null;
  event_date: string; // ISO datetime
  links?: string[] | null;
  display_order?: number;
}

/** Payload for updating a trend event (all fields optional). */
export interface UpdateTrendEventBody {
  short_title?: string;
  long_title?: string;
  survey?: string;
  survey_id?: string;
  description?: string | null;
  image_url?: string | null;
  event_date?: string;
  links?: string[] | null;
  display_order?: number;
}

const trendsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrendsData: builder.query<TrendsData, GetTrendsDataParams>({
      query: ({ ext_survey_id, share, interval, start_date, end_date, selected_survey_answers }: GetTrendsDataParams) => ({
        method: 'get',
        params: {
          interval,
          start_date,
          end_date,
          selected_survey_answers: selected_survey_answers?.join(','),
          client_id: getClientId(),
        },
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/trends`), share ?? false, 'b2b'),
      }),
      serializeQueryArgs: ({ queryArgs: { ext_survey_id, interval, start_date, end_date, selected_survey_answers } }: SerializeQueryArgsParams) => ({
        ext_survey_id,
        interval,
        start_date,
        end_date,
        selected_survey_answers: selected_survey_answers && selected_survey_answers.length > 0
          ? [...selected_survey_answers].sort().join(',')
          : '',
      }),
      transformResponse(response: { item: TrendsData }): TrendsData {
        return response.item;
      },
    }),
    getTrendsEvents: builder.query<TrendEvent[], { ext_survey_id: string; share?: boolean }>({
      query: ({ ext_survey_id, share }) => ({
        method: 'get',
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/trends/events`), share ?? false, 'b2b'),
      }),
      transformResponse(response: { item?: TrendEvent[] }): TrendEvent[] {
        return Array.isArray(response?.item) ? response.item : [];
      },
      providesTags: (result, _error, arg) =>
        result
          ? [
            { type: APITagType.TrendsEvents, id: arg.ext_survey_id },
            ...result.map((e) => ({ type: APITagType.TrendsEvents as const, id: e.id })),
          ]
          : [{ type: APITagType.TrendsEvents, id: arg.ext_survey_id }],
    }),
    createTrendEvent: builder.mutation<TrendEvent, { ext_survey_id: string; body: CreateTrendEventBody }>({
      query: ({ ext_survey_id, body }) => ({
        method: 'post',
        // Viewing/listing is scoped to the survey, but create/edit/delete are not.
        // Backend contract: b2b/survey/trends/events
        url: getUrl(`b2b/survey/trends/events`),
        data: body,
      }),
      transformResponse: (response: { item?: TrendEvent }) => response?.item ?? (response as unknown as TrendEvent),
      invalidatesTags: (_result, _error, arg) => [{ type: APITagType.TrendsEvents, id: arg.ext_survey_id }],
    }),
    updateTrendEvent: builder.mutation<TrendEvent, { ext_survey_id: string; event_id: string; body: UpdateTrendEventBody }>({
      query: ({ ext_survey_id, event_id, body }) => ({
        method: 'patch',
        // Backend contract: b2b/survey/trends/events/<trend_id>
        url: getUrl(`b2b/survey/trends/events/${event_id}`),
        data: body,
      }),
      transformResponse: (response: { item?: TrendEvent }) => response?.item ?? (response as unknown as TrendEvent),
      invalidatesTags: (_result, _error, arg) => [{ type: APITagType.TrendsEvents, id: arg.ext_survey_id }],
    }),
    deleteTrendEvent: builder.mutation<void, { ext_survey_id: string; event_id: string }>({
      query: ({ ext_survey_id, event_id }) => ({
        method: 'delete',
        // Backend contract: b2b/survey/trends/events/<trend_id>
        url: getUrl(`b2b/survey/trends/events/${event_id}`),
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: APITagType.TrendsEvents, id: arg.ext_survey_id }],
    }),
  }),
});

export const {
  useGetTrendsDataQuery,
  useGetTrendsEventsQuery,
  useCreateTrendEventMutation,
  useUpdateTrendEventMutation,
  useDeleteTrendEventMutation,
} = trendsEndpoints;

export { trendsEndpoints };
